import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useT } from "@ec2/i18n/react";
import { shortUrl } from "../lib/links";
import { encodePendingResolve, storePendingResolve } from "../lib/pendingResolve";
import { scanError } from "../lib/errors";
import { useTRPC } from "../lib/api";
import { useActive } from "../state/hooks";
import { ScanResultSheet, type ScanOutcome } from "../components/ScanResultSheet";
import { questFlowPath } from "../lib/quests";
import { LoadingLine } from "../components/common";

/**
 * /q/$code /c/$code /o/$code — native-camera deep links resolve through the
 * same scan.resolve dispatch as the in-app scanner (spec §1.3).
 */
export function ResolveScreen({ kind, code }: { kind: "q" | "c" | "o"; code: string }) {
  const t = useT();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { active, isLoading } = useActive();
  const resolve = useMutation(trpc.scan.resolve.mutationOptions());
  const [outcome, setOutcome] = useState<ScanOutcome | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (isLoading) return;
    if (!active) {
      // Not in an event yet (poster scan before joining): keep the target and
      // send them through join → identity reveal → back to this resolution.
      const pending = { kind, code: code.toUpperCase() };
      storePendingResolve(pending);
      const resolveParam = encodePendingResolve(pending);
      const manualJoin = () => void navigate({ to: "/join", search: { resolve: resolveParam }, replace: true });
      if (kind !== "q") return manualJoin();
      // A quest QR knows its event: offer that event's join confirm (with what
      // the quest pays) instead of asking for the event code.
      if (started.current) return;
      started.current = true;
      queryClient
        .fetchQuery({ ...trpc.event.previewByQuestCode.queryOptions({ code: pending.code }), retry: false })
        .then(({ joinCode }) =>
          navigate({ to: "/j/$code", params: { code: joinCode }, search: { resolve: resolveParam }, replace: true }),
        )
        .catch(manualJoin);
      return;
    }
    if (started.current) return;
    started.current = true;
    const raw = shortUrl(kind, code);
    resolve
      .mutateAsync({ eventId: active.eventId, raw })
      .then((result) => {
        if (result.kind === "quest_open") {
          const path = questFlowPath(result.quest);
          void navigate({ to: path.to, params: path.params, replace: true });
          return;
        }
        void queryClient.invalidateQueries();
        setOutcome({ status: "result", result });
      })
      .catch((err) => setOutcome({ status: "error", error: scanError(err) }));
  }, [isLoading, active, kind, code]);

  const toQuests = () => void navigate({ to: "/quests" });

  return (
    <main className="mx-auto min-h-dvh w-full max-w-md" data-testid="ec-resolve">
      {!outcome && (
        <p role="status" className="px-6 pt-24 text-center text-[13px] font-medium text-fg-muted">
          {t("ec2.resolve.checking")}
        </p>
      )}
      {outcome && <ScanResultSheet outcome={outcome} onScanAgain={() => void navigate({ to: "/scan" })} onClose={toQuests} />}
      {!outcome && <LoadingLine />}
    </main>
  );
}
